import type { ComponentProps, ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import { cn } from "@/common/utils";

/**
 * A group in a page's side navigation (docs, legal): a title over links on a
 * hairline. Pass `onToggle` to make the title a disclosure button.
 */
export function SideNavGroup({
  title,
  open = true,
  onToggle,
  children,
  className,
}: {
  title: ReactNode;
  open?: boolean;
  onToggle?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const titleClass =
    "flex w-full items-center justify-between gap-3 py-2 text-left text-[14px] leading-[1.3] font-medium text-(--g-ink)";

  return (
    <div className={className}>
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={cn(
            titleClass,
            "group cursor-pointer border-0 bg-transparent px-0",
          )}
        >
          {title}
          <ChevronRight
            aria-hidden
            className={cn(
              "size-3.5 flex-none text-(--g-ink-3) transition-transform group-hover:text-(--g-ink)",
              open && "rotate-90",
            )}
          />
        </button>
      ) : (
        <p className={cn(titleClass, "m-0")}>{title}</p>
      )}
      {open && (
        <ul className="m-0 mt-1 mb-3 list-none border-l border-(--g-rule) p-0">
          {children}
        </ul>
      )}
    </div>
  );
}

/** A link in a SideNavGroup; the current page is ink with an accent tick on the hairline. */
export function SideNavLink({
  active = false,
  className,
  ...props
}: ComponentProps<typeof Link> & { active?: boolean }) {
  return (
    <li>
      <Link
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative block py-[6px] pl-4 text-[14px] leading-[1.4] no-underline transition-colors",
          active
            ? "font-medium text-(--g-ink) before:absolute before:inset-y-1 before:-left-px before:w-0.5 before:rounded-full before:bg-(--g-accent)"
            : "text-(--g-ink-2) hover:text-(--g-ink)",
          className,
        )}
        {...props}
      />
    </li>
  );
}
